import React, { useState, useRef, useEffect } from 'react';
import withAuthContext from '../../../context/auth/AuthConsumer';
import { authFetch } from '../../../services/authClient';
import { IAuthContext } from '../../../context/auth/auth-context';
import Sidebar from './Sidebar';
import ChatWindow from '../../enviro-deep-research/ChatWindow';
import { ChatResponse } from '../../../types/deep-research';
import styled from 'styled-components';
import '../../enviro-deep-research/index.scss';
import mountainHeroBg from '../../../assets/mountain-6.jpg';

const PageWrapper = styled.div`
  display: flex;
  width: 100%;
  min-height: 100vh;
  background: #000;
  overflow-y: auto;
`;

const MainContainer = styled.div`
  margin-left: 325px;
  min-height: 100vh;
  background: #000;
  padding: 0;
  flex: 1;
  width: 100%;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url(${mountainHeroBg});
    background-size: cover;
    background-position: center 30%;
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0, 0, 0, 0.55) 0%, rgba(0, 0, 0, 0.65) 45%, rgba(0, 0, 0, 0.75) 100%);
    z-index: 0;
  }

  > * {
    position: relative;
    z-index: 1;
  }
`;

const FormContainer = styled.div`
  max-width: 900px;
  margin: 4em auto;
  background: rgba(10, 10, 12, 0.72);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
  backdrop-filter: blur(10px);
  padding: 28px;
`;

const UploadArea = styled.div`
  border: 1px dashed rgba(148, 163, 184, 0.45);
  border-radius: 12px;
  padding: 48px 28px;
  text-align: center;
  margin-bottom: 28px;
  cursor: pointer;
  transition: border-color 0.2s, background-color 0.2s;
  background: rgba(15, 23, 42, 0.42);

  &:hover {
    border-color: rgba(52, 211, 153, 0.58);
    background: rgba(15, 23, 42, 0.62);
  }
`;

const UploadIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  color: #94a3b8;
`;

const UploadTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #f8fafc;
  margin-bottom: 8px;
`;

const UploadSubtext = styled.p`
  font-size: 14px;
  color: #94a3b8;
  margin: 0;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
`;

const Label = styled.label`
  display: block;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  margin-bottom: 8px;
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  min-height: 46px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  font-size: 14px;
  color: #ecf0f5;
  background: rgba(255, 255, 255, 0.04);
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);

  &:focus {
    outline: none;
    border-color: rgba(52, 211, 153, 0.35);
    background: rgba(255, 255, 255, 0.04);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  font-size: 14px;
  color: #ecf0f5;
  background: rgba(255, 255, 255, 0.04);
  min-height: 120px;
  resize: vertical;
  font-family: var(--sans, 'DM Sans', -apple-system, sans-serif);

  &:focus {
    outline: none;
    border-color: rgba(52, 211, 153, 0.35);
    background: rgba(255, 255, 255, 0.04);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

const StartButton = styled.button`
  background: #3aefb8;
  color: #000;
  border: none;
  border-radius: 12px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 24px;
  box-shadow: 0 8px 20px rgba(52, 211, 153, 0.28);
  transition: background 0.15s ease, transform 0.12s ease;

  &:hover:not(:disabled) {
    background: #63f4ca;
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    background: #63f4ca;
  }

  &:disabled {
    background: rgba(100, 116, 139, 0.45);
    color: rgba(226, 232, 240, 0.7);
    cursor: not-allowed;
    box-shadow: none;
  }
`;

const InfoBox = styled.div`
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.25);
  border-radius: 10px;
  padding: 16px;
  color: #dbe3ef;
  font-size: 14px;
  line-height: 1.6;
`;

const baseApiUrl = `${process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT}/research/get_pred`;

const NewChemicalReview: React.FC<{ context: IAuthContext }> = ({ context: _context }) => {
  const [chemicalName, setChemicalName] = useState('');
  const [intendedUse, setIntendedUse] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<any[]>([]);
  const [chatData, setChatData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState<number | null>(null);
  const [isResearchStarted, setIsResearchStarted] = useState(false);
  const [showForm, setShowForm] = useState(true);
  const interval = useRef<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageTypes = ['image/jpg', 'image/jpeg', 'image/png'];

  // Helper function to convert Blob to base64
  const blobToBase64 = (blob: any) => {
    return new Promise((resolve, reject) => {
      const reader: any = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;

    const selectedFiles = Array.from(e.target.files);
    e.target.value = '';

    const updatedFiles = [...files, ...selectedFiles];
    setFiles(updatedFiles);
    setFilePreviews([]);

    // Process each file for previews
    const previews = await Promise.all(
      updatedFiles.map(async (file) => {
        if (imageTypes.includes(file.type)) {
          const base64 = await blobToBase64(file);
          return { preview: base64, name: file.name, type: file.type };
        }
        return { preview: URL.createObjectURL(file), name: file.name, type: file.type };
      })
    );

    setFilePreviews(previews);
  };

  const removeAttachment = (index: number) => {
    setFiles((prevFiles) => prevFiles.filter((_, i) => i !== index));
    setFilePreviews((prevPreviews) => prevPreviews.filter((_, i) => i !== index));
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const fetchResponse = async (queryText?: string, manualSearchText?: string) => {
    const localSearchText = queryText?.trim() || manualSearchText?.trim() || '';
    const keepAlive = isResearchStarted || localSearchText === 'keep alive';
    if (!keepAlive && localSearchText.trim() === '') return;

    if (!keepAlive) {
      const userMessage = {
        content: localSearchText,
        role: 'user',
        attachFiles: filePreviews || [],
      };
      setChatData((prev) => [...prev, userMessage]);
    }
    setIsLoading(true);

    try {
      const chatStatus = chatId || keepAlive ? 'ongoing' : 'new';
      const localUserId = localStorage.getItem('uid');
      const cancelToken = new AbortController();

      // Decide whether to use 'POST' or 'GET' based on the presence of files
      const method = Array.isArray(files) && files.length && !keepAlive ? 'POST' : 'GET';

      let url = baseApiUrl;
      const fetchOptions: any = {
        method,
        signal: cancelToken.signal,
      };

      if (method === 'POST') {
        // Build FormData for POST requests
        const formData = new FormData();
        formData.append('q', keepAlive ? 'keep alive' : localSearchText);
        formData.append('chatStatus', chatStatus);

        if (localUserId) {
          formData.append('userId', encodeURIComponent(localUserId));
        }
        if (chatId) {
          formData.append('chatId', chatId.toString());
        }

        // Add files to FormData
        if (Array.isArray(files) && files.length) {
          files.forEach((file: any) => formData.append('files', file));
        }

        fetchOptions.body = formData;
        // Reset files after sending
        setFiles([]);
      } else {
        // Build query params for GET requests
        const q = encodeURIComponent(keepAlive ? 'keep alive' : localSearchText);
        const userId = localUserId ? encodeURIComponent(localUserId) : '';

        const queryParams = new URLSearchParams({
          chatStatus,
          q,
        });

        if (userId) {
          queryParams.append('userId', userId);
        }
        if (chatId) {
          queryParams.append('chatId', chatId.toString());
        }

        url = `${baseApiUrl}?${queryParams.toString()}`;
      }

      if (chatStatus === 'ongoing' && !isResearchStarted) {
        setIsResearchStarted(true);
      }

      const response = await authFetch(url, fetchOptions);
      const data = await response.json() as ChatResponse;
      if (!data) {
        return;
      }
      setChatId(typeof data.chat_id === 'string' ? parseInt(data.chat_id, 10) : data.chat_id);
      const assistantMessage = {
        content: (data.content || 'No response received.').replace(/\n/g, '<br/>'),
        status: data.status,
        pdf: data.pdf,
      };

      setChatData((prev) => {
        const existingItem = prev.find((item) => item.status === 'executing');
        if (!existingItem) {
          return [...prev, assistantMessage];
        }
        existingItem.content = assistantMessage.content;
        existingItem.pdf = assistantMessage.pdf;
        return prev;
      });
      if (data.status === 'completed') {
        setIsResearchStarted(false);
        if (interval.current) {
          window.clearInterval(interval.current);
        }
      }
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log('Error fetching response:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (chatId && isResearchStarted) {
      setIsLoading(true);
      const id = window.setInterval(() => {
        fetchResponse('keep alive');
      }, 30000);
      interval.current = id;
      return () => {
        if (interval.current) {
          window.clearInterval(interval.current);
        }
      };
    }
    // eslint-disable-next-line
  }, [chatId, isResearchStarted]);

  // Ensure the page can scroll even when accessed directly
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'auto';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  const handleStartReview = () => {
    if (!chemicalName.trim()) {
      return;
    }

    // Build query string from form data
    let queryText = `New Chemical Review for: ${chemicalName}`;
    if (intendedUse.trim()) {
      queryText += `\n\nIntended Use & Quantity: ${intendedUse}`;
    }

    // Hide form and show chat
    setShowForm(false);

    // Start the research
    fetchResponse(queryText);
  };

  return (
    <PageWrapper>
      <Sidebar />
      <MainContainer>
        {showForm ? (
          <FormContainer>
            <UploadArea onClick={handleUploadClick}>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                onChange={handleFileSelect}
                className="cmn-display-none"
              />
              <UploadIcon>📄</UploadIcon>
              <UploadTitle>Upload Safety Data Sheet (SDS)</UploadTitle>
              <UploadSubtext>Click to browse or drag and drop • PDF, DOC, or image files</UploadSubtext>
            </UploadArea>

            {filePreviews.length > 0 && (
              <div className="cmn-chem-review-previews">
                {filePreviews.map((file, index) => (
                  <div key={index} className="cmn-chem-review-preview-chip">
                    {imageTypes.includes(file.type) ? (
                      <img src={file.preview} alt={file.name} className="cmn-chem-review-preview-img" />
                    ) : (
                      <span className="cmn-chem-review-preview-icon">📄</span>
                    )}
                    <span className="cmn-chem-review-preview-name">{file.name}</span>
                    <button
                      onClick={() => removeAttachment(index)}
                      className="cmn-chem-review-preview-remove"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <FormGroup>
              <Label>Chemical Name</Label>
              <Input
                type="text"
                placeholder="e.g., Glutaraldehyde 50%"
                value={chemicalName}
                onChange={(e) => setChemicalName(e.target.value)}
              />
            </FormGroup>

            <FormGroup>
              <Label>Intended Use & Quantity (Optional)</Label>
              <TextArea
                placeholder="e.g., Cooling tower biocide treatment. Approximately 200 gallons will be stored on-site."
                value={intendedUse}
                onChange={(e) => setIntendedUse(e.target.value)}
              />
            </FormGroup>

            <StartButton onClick={handleStartReview} disabled={!chemicalName.trim()}>
              <span>Start Review</span>
              <span>→</span>
            </StartButton>

            <InfoBox>
              <strong>What you'll receive:</strong> Comprehensive analysis including hazard classification,
              regulatory triggers (PSM, RMP, air permits, waste), required controls, PPE requirements,
              storage specifications, and environmental considerations.
            </InfoBox>
          </FormContainer>
        ) : (
          <div className="enviro-deep-research cmn-chem-review-chat-shell">
            <div className="search-area-container cmn-chem-review-chat-area">
              <ChatWindow
                chatData={chatData}
                isLoading={isLoading || isResearchStarted}
                onStartResearch={() => fetchResponse('Please go ahead')}
                bubbleTheme="light"
              />
            </div>
          </div>
        )}
      </MainContainer>
    </PageWrapper>
  );
};

export default withAuthContext(NewChemicalReview);

